/**
 * قابلیت‌های اختصاصی شرکت‌ها (موقت تا پیکربندی از بک‌اند).
 * به‌جای پخش if(company_id) در UI، از hasCompanyFeature استفاده کنید.
 */

/** چاپروم — فعلاً روی env فعلی company_id=15 */
export const CHAPROOM_COMPANY_IDS = [15];

const COMPANY_FEATURES = {
  15: [
    /** سوال «آیا فایل نیاز به تبدیل دارد؟» هنگام آپلود فایل وظیفه/زیروظیفه */
    'task.fileNeedConvertPrompt',
    /** باکس اطلاعات پستی سفارش در مشاهده وظیفه */
    'task.postalInfoBox',
    /** دکمه بررسی وضعیت کنسل سفارش در مشاهده وظیفه */
    'task.chaproomStatusCheck',
    /** مدال کد رهگیری / نحوه ارسال هنگام انتقال به «بسته بندی و ارسال» */
    'task.chaproomPackagingShipping',
  ],
};

/** شماره سفارش چاپروم از order_fields (بدون #) */
export const extractChaproomOrderId = (task) => {
  const cf = task?.custom_fields;
  const orderFields = Array.isArray(cf?.order_fields)
    ? cf.order_fields
    : Array.isArray(task?.order_fields)
      ? task.order_fields
      : [];

  const orderField = orderFields.find(
    (f) => String(f?.name || '').trim().toLowerCase() === 'order_id',
  );
  const raw = orderField?.value;
  if (raw != null && String(raw).trim()) {
    const digits = String(raw).replace(/[^\d]/g, '');
    if (digits) return digits;
  }
  return null;
};

export const isChaproomOrderCanceled = (res) => {
  const v = res?.is_canceled ?? res?.data?.is_canceled ?? res?.is_cancelled ?? res?.data?.is_cancelled;
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
};

/** نرمال‌سازی نام ستون کانبان برای مقایسه فارسی */
export const normalizeFaColumnName = (value) =>
  String(value || '')
    .replace(/\u200c/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** ستون «بسته بندی و ارسال» چاپروم */
export const isChaproomPackagingColumn = (columnOrName) => {
  const raw =
    typeof columnOrName === 'string'
      ? columnOrName
      : columnOrName?.column_name || columnOrName?.name || '';
  return normalizeFaColumnName(raw) === normalizeFaColumnName('بسته بندی و ارسال');
};

/** پارامترهای ارسال در change-status */
export const isChaproomShippingExtra = (extra) =>
  extra != null &&
  typeof extra === 'object' &&
  !Array.isArray(extra) &&
  ('trackingCode' in extra || 'postageMethod' in extra);

export const CHAPROOM_POSTAGE_METHOD_OPTIONS = [
  { value: 'پست ویژه', label: 'پست ویژه' },
  { value: 'ارسال با پیک', label: 'ارسال با پیک' },
];

export const isChaproomCompany = (companyId) =>
  CHAPROOM_COMPANY_IDS.some((id) => String(id) === String(companyId));

export const hasCompanyFeature = (companyId, feature) => {
  if (companyId == null || !feature) return false;
  const list = COMPANY_FEATURES[Number(companyId)] || COMPANY_FEATURES[String(companyId)] || [];
  return list.includes(feature);
};

/** استخراج درصد از ریسپانس convert-progress */
export const parseConvertProgressPercent = (res) => {
  if (res == null) return null;
  const candidates = [
    res.progress,
    res.percent,
    res.percentage,
    res.convert_progress,
    res.data?.progress,
    res.data?.percent,
    res.data?.percentage,
    res.data?.convert_progress,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.min(100, Math.max(0, Math.round(n)));
  }
  return null;
};

export const isConvertProgressComplete = (res, percent) => {
  const status = String(
    res?.convert_status ||
      res?.status_convert ||
      res?.conversion_status ||
      res?.data?.convert_status ||
      res?.data?.conversion_status ||
      res?.state ||
      '',
  ).toLowerCase();
  if (['complete', 'completed', 'done', 'finished'].includes(status)) return true;
  return percent != null && percent >= 100;
};

/** رکورد فایل واقعی — نه id خام task/sub_task در data */
const isFileLikeRecord = (item) => {
  if (item == null) return false;
  if (typeof item === 'number' || typeof item === 'string') return true;
  if (typeof item !== 'object') return false;
  if (item.file_id != null || item.task_file_id != null || item.sub_task_file_id != null) {
    return true;
  }
  if (item.id == null) return false;
  return (
    item.filename != null ||
    item.hashname != null ||
    item.file_url != null ||
    item.download_url != null ||
    item.mime_type != null ||
    item.file != null ||
    item.size != null ||
    // لیست فایل‌های وظیفه از GET معمولاً name/filename دارند
    (item.name != null && (item.user_id != null || item.created_at != null || item.company_id != null))
  );
};

const resolveUploadedFileId = (f) => {
  if (f == null) return null;
  if (typeof f === 'number' || typeof f === 'string') return f;
  return f.file_id ?? f.task_file_id ?? f.sub_task_file_id ?? (isFileLikeRecord(f) ? f.id : null);
};

const mapToUploadedFiles = (list, { trustIdOnly = false } = {}) =>
  (list || [])
    .map((f, index) => {
      if (f == null) return null;
      if (typeof f === 'number' || typeof f === 'string') {
        return { id: f, name: `فایل ${f}` };
      }
      const id = trustIdOnly
        ? f.file_id ?? f.task_file_id ?? f.sub_task_file_id ?? f.id
        : resolveUploadedFileId(f);
      if (id == null) return null;
      return {
        id,
        name: f.filename || f.name || f.hashname || `فایل ${index + 1}`,
      };
    })
    .filter(Boolean);

/**
 * استخراج لیست فایل‌های آپلودشده (id + نام) از ریسپانس آپلود.
 * برای polling تبدیل — فقط آیدی خود فایل، نه task_id / sub_task_id.
 *
 * chunk-upload نمونه:
 * { status, message, file: {...}, task_id, file_id, need_convert }
 */
export const extractUploadedFiles = (res) => {
  if (!res) return [];

  const asList = (value, { trustIdOnly = false } = {}) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      if (trustIdOnly) {
        return value.filter(
          (item) =>
            item != null &&
            (typeof item === 'number' ||
              typeof item === 'string' ||
              item.id != null ||
              item.file_id != null ||
              item.task_file_id != null ||
              item.sub_task_file_id != null),
        );
      }
      return value.filter(isFileLikeRecord);
    }
    if (typeof value === 'object') {
      if (trustIdOnly && (value.id != null || value.file_id != null)) return [value];
      if (isFileLikeRecord(value)) return [value];
    }
    return [];
  };

  // ۰) file_id صریح از chunk-upload (اولویت اول)
  const directFileId = res.file_id ?? res.data?.file_id;
  if (directFileId != null && directFileId !== '') {
    const fileObj = res.file || res.data?.file || {};
    return [
      {
        id: directFileId,
        name:
          fileObj.filename ||
          fileObj.name ||
          fileObj.hashname ||
          `فایل ${directFileId}`,
      },
    ];
  }

  // ۱) آبجکت file در ریسپانس chunk-upload
  const fileObjCandidates = [res.file, res.data?.file];
  for (const fileObj of fileObjCandidates) {
    if (fileObj && typeof fileObj === 'object') {
      const mapped = mapToUploadedFiles([fileObj], {
        trustIdOnly: fileObj.id != null || fileObj.file_id != null,
      });
      if (mapped.length) return mapped;
    }
  }

  // ۲) فایل‌های تازه‌از-GET که خودمان ساخته‌ایم
  const trusted = [
    asList(res.__newFiles, { trustIdOnly: true }),
  ];
  for (const list of trusted) {
    const mapped = mapToUploadedFiles(list, { trustIdOnly: true });
    if (mapped.length) return mapped;
  }

  // ۳) کلیدهای صریح فایل در ریسپانس آپلود
  const fileKeyCandidates = [
    res.files,
    res.uploaded_files,
    res.uploadedFiles,
    res.task_files,
    res.sub_task_files,
    res.subtask_files,
    res.data?.files,
    res.data?.uploaded_files,
    res.data?.uploadedFiles,
    res.data?.task_files,
    res.data?.sub_task_files,
    res.data?.subtask_files,
  ];

  for (const c of fileKeyCandidates) {
    const next = asList(c, { trustIdOnly: false });
    const mapped = mapToUploadedFiles(next);
    if (mapped.length) return mapped;
  }

  // ۴) آرایهٔ صریح file_ids
  const idLists = [res.file_ids, res.data?.file_ids];
  for (const ids of idLists) {
    if (Array.isArray(ids) && ids.length) {
      return ids.filter((id) => id != null).map((id) => ({ id, name: `فایل ${id}` }));
    }
  }

  // ۵) از آرایهٔ responses (چند فایل chunk)
  if (Array.isArray(res.responses) && res.responses.length) {
    const fromResponses = res.responses.flatMap((r) => extractUploadedFiles(r));
    if (fromResponses.length) return fromResponses;
  }

  // ۶) جست‌وجوی عمیق — فقط رکوردهای شبیه فایل (نه data.id خام)
  if (typeof res === 'object') {
    const queue = [res];
    const seen = new Set();
    while (queue.length) {
      const node = queue.shift();
      if (!node || typeof node !== 'object' || seen.has(node)) continue;
      seen.add(node);
      if (Array.isArray(node)) {
        const fileLike = node.filter(isFileLikeRecord);
        const mapped = mapToUploadedFiles(fileLike);
        if (mapped.length) return mapped;
        node.forEach((item) => {
          if (item && typeof item === 'object') queue.push(item);
        });
      } else {
        Object.entries(node).forEach(([key, v]) => {
          if (['task', 'sub_task', 'subtask', 'project', 'user', 'employee'].includes(key)) {
            return;
          }
          if (v && typeof v === 'object') queue.push(v);
        });
      }
    }
  }

  return [];
};

/** فایل‌های جدید نسبت به لیست قبلی (fallback بعد از آپلود) */
export const diffNewFiles = (beforeFiles = [], afterFiles = [], uploadedCount = 0) => {
  const beforeIds = new Set((beforeFiles || []).map((f) => String(f?.id)).filter(Boolean));
  const after = Array.isArray(afterFiles) ? afterFiles : [];
  const added = after.filter((f) => f?.id != null && !beforeIds.has(String(f.id)));
  if (added.length) return added;
  if (uploadedCount > 0 && after.length) {
    return [...after]
      .sort((a, b) => Number(b.id) - Number(a.id))
      .slice(0, uploadedCount);
  }
  return [];
};
